
import Component from 'vue-class-component';
import { Prop, Vue, Watch } from 'vue-property-decorator';
import {
  GeneralInvoicingTargetEnum,
  toGenericInvoicingTarget,
  InvoicingTargetEnum,
} from '@gid/models';
import CrudPicklistMultiSelect from '@gid/vue-common/components/filters/CrudPicklistMultiSelect.vue';
import RichTextEditor from '@gid/vue-common/components/RichTextEditor.vue';
import PartialCheckbox, {
  PartialStateEnum,
} from './common/PartialCheckbox.vue';
import { SelectActionsEnum } from '@gid/models/dist/entities/job-selection';
import {
  onPartialSelectionChange,
  loadPartialSelection,
} from '../../utils/partial_selection';

export type CustomServiceLocalType = {
  id?: number;
  measure: string;
  quantity: number;
  invoicing_target: string;
  price_purchasing: number | null;
  price_lists: number | null;
  price_selling: number | null;
  service_type: string;
  selections: any;
  sort_index: number | null;
  headline: string;
};

@Component({
  components: {
    CrudPicklistMultiSelect,
    RichTextEditor,
    PartialCheckbox,
  },
})
export default class CustomService extends Vue {
  @Prop() readonly jobId!: string;
  @Prop({ type: Object }) readonly item!: CustomServiceLocalType;
  @Prop({ type: Number }) readonly index!: number;
  @Prop() editable!: boolean;
  @Prop() readonly!: boolean;
  @Prop() showServiceHints!: boolean;
  @Prop() context!: string;
  @Prop() custom_services!: Array<CustomServiceLocalType>;
  @Prop() invoicing_target!: GeneralInvoicingTargetEnum;
  @Prop() editInvoicingTarget!: boolean;
  @Prop() allowSelection?: boolean;
  @Prop({ type: Array, default: () => [] }) selections?: any;
  serviceType = '';

  @Watch('serviceType')
  onServiceTypeChange(val: string) {
    this.item.service_type = val;
    val
      ? this.$store.dispatch('CUSTOM_SERVICE_PUSH_ERROR', { index: this.index })
      : this.$store.dispatch('CUSTOM_SERVICE_PUSH_ERROR', {
          index: this.index,
          errorString: 'order-details.custom-service-error.service-type',
        }); // if type null set error message bellow field
  }

  formatPrice(value: number | string | null) {
    if (typeof value === 'string') {
      value = parseFloat(value.replace(',', '.'));
    }
    return value == null || Number.isNaN(value) ? null : value;
  }

  formatSortIndex(value: number | string | null) {
    if (typeof value === 'string') {
      value = parseFloat(value.replace(',', '.'));
    }
    return value == null || Number.isNaN(value) ? null : value;
  }

  updatePriceLists(value: number | string | null) {
    this.item.price_lists = this.formatPrice(value);
  }

  updatePriceSelling(value: number | string | null) {
    this.item.price_selling = this.formatPrice(value);
  }

  updatePricePurchasing(value: number | string | null) {
    this.item.price_purchasing = this.formatPrice(value);
  }

  updateSortIndex(value: number | string | null) {
    this.item.sort_index = this.formatSortIndex(value);
  }

  created() {
    if (!this.item.measure || !this.item.quantity) {
      this.item.measure = 'Count';
      this.item.quantity = 1;
      this.item.invoicing_target = this.invoicing_target;
    }
    if (!this.item.invoicing_target) {
      this.item.invoicing_target = this.opportunityInvoicingTarget;
    }
    if (
      (!this.item.service_type && this.context != 'partner') ||
      this.item.service_type
    ) {
      this.serviceType = this.item.service_type || '170.Material_und_Arbeit';
    }
  }

  get opportunityInvoicingTarget() {
    return toGenericInvoicingTarget(
      this.$store.getters.jobOpportunity(this.jobId)?.invoicing_target,
    );
  }

  deleteItem() {
    this.$emit('delete', this.index);
  }
  get itemPricePurchasing() {
    return (pricePurchasing) => {
      if (pricePurchasing) {
        return pricePurchasing;
      }
      return 0;
    };
  }

  get limitedOptions() {
    if (this.context == 'partner') {
      return [
        '090.Materialkosten',
        '020.Installation',
        '170.Material_und_Arbeit',
      ];
    }
    return [];
  }

  get invoicingTargets() {
    return Object.values(GeneralInvoicingTargetEnum);
  }
  get price_selling_style() {
    let style = {};
    if (
      this.item.invoicing_target &&
      [
        GeneralInvoicingTargetEnum.AFFILIATE,
        GeneralInvoicingTargetEnum.BRAND,
        GeneralInvoicingTargetEnum.BRAND_1,
        GeneralInvoicingTargetEnum.BRAND_2,
      ].includes(this.item.invoicing_target as GeneralInvoicingTargetEnum)
    ) {
      style = { ...style, 'background-color': '#D4EDDA' };
    }
    return style;
  }

  get price_lists_style() {
    let style = {};
    if (
      this.item.invoicing_target &&
      this.item.invoicing_target === GeneralInvoicingTargetEnum.CUSTOMER
    ) {
      style = { 'background-color': '#D4EDDA' };
    }
    return style;
  }

  get price_purchasing_style() {
    let style = null;

    return style;
  }

  measure(measure) {
    if (measure && !['Count', 'Piece', 'Euro', '0,5 h'].includes(measure)) {
      return measure;
    } else {
      return '×';
    }
  }

  // Computed property to load partial selection on Job
  get partiallySelection() {
    return loadPartialSelection(
      this.item?.selections,
      SelectActionsEnum.PARTIAL_INVOICING,
    );
  }

  onPartialInvoiceChange(
    isSelected,
    action = SelectActionsEnum.PARTIAL_INVOICING,
  ) {
    const state = isSelected ? PartialStateEnum.SCHEDULED : null;
    const service = {
      selected: isSelected,
      action,
      state,
      item: this.item.id, // c_service id
    };
    this.item.selections = onPartialSelectionChange(
      this.item.selections,
      service,
      'c_service',
    );
  }
  get showInvoicingTarget() {
    return (
      this.item?.invoicing_target === GeneralInvoicingTargetEnum.BRAND_2 ||
      this.item?.invoicing_target === GeneralInvoicingTargetEnum.CUSTOMER_SECOND
    );
  }

  get currentInvoicingTarget() {
    if (!this.item?.invoicing_target) return '';
    if (
      this.item?.invoicing_target === GeneralInvoicingTargetEnum.CUSTOMER_SECOND
    )
      return 'customer_2';

    return this.item?.invoicing_target;
  }
}
